import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { AppIcon } from '../../components/AppIcon';
import { formatDuration } from '../../features/pilotMath/format';
import { pilotSummary } from '../../features/pilotMath/store';
import { usePilotState } from '../../features/pilotMath/usePilotState';
import { ensureCurrentMathLevelProgress, type LevelProgressSummary } from '../../features/progression/levelProgress';
import { getSupabaseRuntimeIdentity } from '../../features/runtime/supabaseAdapter';
import { env } from '../../lib/env';
import { getSupabaseClient } from '../../lib/supabase';

type SubjectCardProps = {
  icon: string;
  title: string;
  detail: string;
  tone: string;
  progress?: number;
  levelLabel?: string;
  status?: string;
  to?: string;
};

function SubjectCard({ icon, title, detail, tone, progress, levelLabel, status, to }: SubjectCardProps) {
  const body = (
    <article className={`subject-card subject-card--${tone}${to ? ' subject-card--clickable' : ''}`}>
      <div className="subject-card__icon">{icon}</div>
      <div className="subject-card__body">
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      {typeof progress === 'number' ? (
        <div className="subject-card__progress">
          <b>{levelLabel ? `${levelLabel} · ` : ''}{Math.round(progress)}%</b>
          <div className="mini-track"><span style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} /></div>
        </div>
      ) : <span className="subject-card__status">{status || 'Segera hadir'}</span>}
    </article>
  );
  return to ? <Link className="subject-card-link" to={to}>{body}</Link> : body;
}

function SubjectGrid({ mathProgress, mathLevel }: { mathProgress: number; mathLevel: string }) {
  return (
    <div className="subject-grid">
      <SubjectCard icon="➗" title="Matematika" detail="Latihan hitung dan pemahaman konsep" tone="math" progress={mathProgress} levelLabel={mathLevel} to="/student/study-v2" />
      <SubjectCard icon="ABC" title="English" detail="Mendengar, melihat, membaca, dan menulis" tone="english" status="Segera hadir" />
      <SubjectCard icon="⚗" title="IPA" detail="Belajar memahami sains di sekitar kita" tone="science" status="Segera hadir" />
      <SubjectCard icon="🌏" title="IPS" detail="Mengenal manusia, tempat, dan masyarakat" tone="social" status="Segera hadir" />
      <SubjectCard icon="ع" title="Bahasa Arab" detail="Kosakata, bunyi, dan struktur dasar" tone="arabic" status="Segera hadir" />
      <SubjectCard icon="📖" title="Bahasa Indonesia" detail="Membaca, memahami, dan menulis" tone="indo" status="Segera hadir" />
    </div>
  );
}

type CloudAssignment = {
  id: string;
  status: string;
  assigned_date: string;
  assignment_type: string;
};

type CloudSkillProgress = { evidence_count: number };
type CloudEvidence = { active_duration_ms: number; occurred_at: string };

function assignmentStatusLabel(status?: string) {
  if (status === 'completed') return 'Selesai';
  if (status === 'submitted') return 'Sudah dikirim';
  if (status === 'started') return 'Sedang dikerjakan';
  if (status === 'assigned') return 'Siap dikerjakan';
  return 'Siap belajar';
}

function CloudStudentDashboard() {
  const [displayName, setDisplayName] = useState('Murid');
  const [assignment, setAssignment] = useState<CloudAssignment | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [progress, setProgress] = useState<CloudSkillProgress[]>([]);
  const [evidence, setEvidence] = useState<CloudEvidence[]>([]);
  const [levelProgress, setLevelProgress] = useState<LevelProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const client = getSupabaseClient();
        if (!client) throw new Error('BelajarKu belum terhubung ke layanan belajar.');
        const identity = await getSupabaseRuntimeIdentity();
        if (!identity?.student) throw new Error('Profil murid belum tersedia.');
        if (cancelled) return;
        setDisplayName(identity.student.displayName);

        const [assignmentResult, progressResult, evidenceResult, currentLevel] = await Promise.all([
          client
            .from('assignments')
            .select('id,status,assigned_date,assignment_type')
            .eq('student_id', identity.student.id)
            .neq('status', 'cancelled')
            .order('assigned_date', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1),
          client
            .from('student_skill_progress')
            .select('evidence_count')
            .eq('student_id', identity.student.id),
          client
            .from('skill_evidence_events')
            .select('active_duration_ms,occurred_at')
            .eq('student_id', identity.student.id)
            .order('occurred_at', { ascending: false })
            .limit(100),
          ensureCurrentMathLevelProgress(),
        ]);

        if (assignmentResult.error) throw assignmentResult.error;
        if (progressResult.error) throw progressResult.error;
        if (evidenceResult.error) throw evidenceResult.error;
        if (cancelled) return;

        const latestAssignment = ((assignmentResult.data ?? [])[0] ?? null) as CloudAssignment | null;
        setAssignment(latestAssignment);
        setProgress((progressResult.data ?? []) as CloudSkillProgress[]);
        setEvidence((evidenceResult.data ?? []) as CloudEvidence[]);
        setLevelProgress(currentLevel);

        if (latestAssignment) {
          const items = await client.from('assignment_items').select('id').eq('assignment_id', latestAssignment.id);
          if (items.error) throw items.error;
          if (!cancelled) setQuestionCount((items.data ?? []).length);
        }
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Beranda belum dapat dimuat lengkap.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const lifetimeEvidenceCount = useMemo(() => progress.reduce((sum, row) => sum + Number(row.evidence_count || 0), 0), [progress]);
  const recentActiveMs = useMemo(() => evidence.reduce((sum, row) => sum + Number(row.active_duration_ms || 0), 0), [evidence]);
  const currentLevelPercent = Math.round(levelProgress?.progressPercent ?? 0);
  const currentLevelLabel = levelProgress?.levelName || 'Level C';
  const checkpointCompleted = levelProgress?.checkpointCompleted ?? 0;
  const checkpointTarget = levelProgress?.checkpointTarget ?? 0;
  const checkpointInProgress = levelProgress?.checkpointInProgress ?? 0;
  const isCompleted = assignment?.status === 'completed';
  const hasStarted = assignment?.status === 'started' || assignment?.status === 'submitted';

  return (
    <div className="dashboard-page">
      <section className="dashboard-heading">
        <div>
          <span className="page-kicker">BELAJARKU</span>
          <h1>Halo, {displayName}! 👋</h1>
          <p className="muted">Hari ini kita maju sedikit lagi.</p>
        </div>
        <div className="record-pill record-pill--level"><AppIcon name="star" /><b>{currentLevelLabel}</b><small>{currentLevelPercent}% perjalanan level</small></div>
      </section>

      {error ? <div className="feedback feedback--error"><strong>Beranda belum lengkap.</strong><p>{error}</p></div> : null}

      <section className="today-panel">
        <div className="today-panel__copy">
          <div className="section-label"><AppIcon name="calendar" /> Belajar Hari Ini</div>
          <h2>{loading ? 'Menyiapkan pelajaranmu…' : isCompleted ? 'Latihan Matematika terakhir sudah selesai.' : hasStarted ? 'Lanjutkan latihan Matematika.' : 'Latihan Matematika siap dimulai.'}</h2>
          <p>
            {loading
              ? 'Sebentar, BelajarKu sedang menyiapkan halaman belajarmu.'
              : assignment
                ? `${assignmentStatusLabel(assignment.status)} · ${assignment.assigned_date}. Kerjakan sampai selesai. Jika ada yang perlu diperbaiki, BelajarKu akan menunjukkan soalnya setelah semua jawaban dikirim.`
                : 'Belum ada latihan yang dibuka hari ini. Tekan Mulai Belajar untuk menyiapkannya.'}
          </p>
          <div className="button-row">
            {!isCompleted ? (
              <Link className="btn btn--primary btn--large" to="/student/study-v2">
                {hasStarted ? 'Lanjutkan Belajar' : 'Mulai Belajar'} <AppIcon name="chevronRight" width={18} />
              </Link>
            ) : (
              <Link className="btn btn--primary" to="/student/progress">Lihat Hasil</Link>
            )}
            <Link className="btn" to="/student/progress">Lihat Progress</Link>
          </div>
        </div>
        <div className="today-panel__progress">
          <div className="progress-ring" style={{ '--progress': `${currentLevelPercent * 3.6}deg` } as CSSProperties}>
            <div><strong>{currentLevelPercent}%</strong><span>{currentLevelLabel}</span></div>
          </div>
          <div className="today-stats">
            <div><AppIcon name="target" /><span><b>{questionCount || '—'}</b><small>soal latihan terakhir</small></span></div>
            <div><AppIcon name="clock" /><span><b>{formatDuration(recentActiveMs)}</b><small>waktu belajar tersimpan</small></span></div>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-heading">
          <div><span className="page-kicker">MATA PELAJARAN</span><h2>Ruang belajarmu</h2></div>
          <Link className="text-link" to="/student/subjects">Lihat semua <AppIcon name="chevronRight" width={16} /></Link>
        </div>
        <SubjectGrid mathProgress={currentLevelPercent} mathLevel={currentLevelLabel} />
      </section>

      <section className="dashboard-grid">
        <article className="card dashboard-card dashboard-card--level">
          <div className="section-label"><AppIcon name="chart" /> Perjalanan {currentLevelLabel}</div>
          <div className="metric-strip">
            <div><strong>{currentLevelPercent}%</strong><span>Progress level</span></div>
            <div><strong>{checkpointTarget ? `${checkpointCompleted}/${checkpointTarget}` : '—'}</strong><span>Tahap selesai</span></div>
            <div><strong>{formatDuration(recentActiveMs)}</strong><span>Waktu belajar</span></div>
          </div>
          <p className="student-friendly-note">Setiap level dimulai dari 0%. Progress bertambah saat satu tahap kurikulum selesai, bukan setiap kali satu soal dijawab.{checkpointInProgress > 0 ? ` ${checkpointInProgress} tahap sedang kamu kerjakan.` : ''}</p>
          <Link className="text-link" to="/student/progress">Lihat perjalanan lengkap <AppIcon name="chevronRight" width={16} /></Link>
        </article>

        <article className="card dashboard-card">
          <div className="section-label"><AppIcon name="history" /> Riwayat Belajarmu</div>
          <h3>Usahamu tersimpan, bukan hanya nilai akhirnya.</h3>
          <p className="muted">Jawaban pertama, perbaikan, waktu belajar, dan tulisanmu dapat dilihat kembali.</p>
          <div className="evidence-chips"><span>✍ Tulisan</span><span>↺ Perbaikan</span><span>⏱ Waktu belajar</span></div>
          <small className="muted">{lifetimeEvidenceCount} aktivitas kemampuan telah tercatat sepanjang perjalananmu.</small>
        </article>
      </section>
    </div>
  );
}

function LocalStudentDashboard() {
  const [state] = usePilotState();
  const summary = pilotSummary(state);
  const current = state.questions[state.currentIndex];
  const mathProgress = summary.totalQuestions > 0 ? Math.round((summary.completed / summary.totalQuestions) * 100) : 0;
  const hasStarted = state.status === 'in_progress' || summary.completed > 0;

  return (
    <div className="dashboard-page">
      <section className="dashboard-heading"><div><span className="page-kicker">BELAJARKU</span><h1>Halo, {state.student.displayName}! 👋</h1><p className="muted">Hari ini kita maju sedikit lagi.</p></div></section>
      <section className="today-panel">
        <div className="today-panel__copy">
          <div className="section-label"><AppIcon name="calendar" /> Belajar Hari Ini</div>
          <h2>{state.status === 'completed' ? 'Target Matematika hari ini selesai.' : hasStarted ? 'Lanjutkan latihan Matematika.' : 'Latihan Matematika sudah siap.'}</h2>
          <p>{state.status === 'completed' ? 'Bagus. Proses belajarmu sudah tersimpan.' : current ? `${current.skillLabel}. Kerjakan satu per satu sampai selesai.` : 'Latihan hari ini siap dibuka.'}</p>
          <div className="button-row"><Link className="btn btn--primary btn--large" to="/student/study-v2">{hasStarted ? 'Lanjutkan Belajar' : 'Mulai Belajar'} <AppIcon name="chevronRight" width={18} /></Link><Link className="btn" to="/student/progress">Lihat Progress</Link></div>
        </div>
        <div className="today-panel__progress"><div className="progress-ring" style={{ '--progress': `${mathProgress * 3.6}deg` } as CSSProperties}><div><strong>{mathProgress}%</strong><span>Level C</span></div></div><div className="today-stats"><div><AppIcon name="target" /><span><b>{summary.completed}/{summary.totalQuestions}</b><small>soal</small></span></div><div><AppIcon name="clock" /><span><b>{formatDuration(summary.totalActiveMs)}</b><small>waktu belajar</small></span></div></div></div>
      </section>
      <section className="dashboard-section"><div className="section-heading"><div><span className="page-kicker">MATA PELAJARAN</span><h2>Ruang belajarmu</h2></div><Link className="text-link" to="/student/subjects">Lihat semua <AppIcon name="chevronRight" width={16} /></Link></div><SubjectGrid mathProgress={mathProgress} mathLevel="Level C" /></section>
    </div>
  );
}

export function StudentDashboard() {
  if (env.supabaseUrl && env.supabasePublishableKey) return <CloudStudentDashboard />;
  return <LocalStudentDashboard />;
}
